/**
 * 50/30/20 model — needs/wants/savings buckets allocated as fixed
 * fractions of period income. Spending is tracked per bucket.
 */

import type { AppData, Bucket503020 } from '../../data/types';
import type { PayPeriod } from '../payPeriod';
import {
  computeRolloverAmount,
  resolvePeriodIncome,
  roundMoney,
  sumExpensesInRange,
  sumExpensesInRangeWhere,
  sumIncomeInRange,
  sumSpendingWithScheduledInRange,
} from './shared';
import { _classifyDailyStatus, type DailyStatus } from './daily';

export interface BucketResult {
  allocated: number;
  spent: number;
  remaining: number;
  /** 0..1 — clamped progress for bars. */
  progress: number;
}

export interface FiftyThirtyTwentyResult {
  period: PayPeriod;
  // Running-balance fields (fallback when no accounts are linked)
  availableToday: number;
  dailyIncomeRate: number;
  remainingThisPeriod: number;
  dayInPeriod: number;
  totalDays: number;
  daysRemaining: number;
  spentToday: number;
  spentThisPeriod: number;
  status: DailyStatus;
  statusLabel: string;
  periodIncome: number;
  rolloverAmount: number;
  // 50/30/20 specific
  needs: BucketResult;
  wants: BucketResult;
  savings: BucketResult;
}

const BUCKET_RATIOS: Record<Bucket503020, number> = {
  needs: 0.5,
  wants: 0.3,
  savings: 0.2,
};

function computeBucket(
  data: AppData,
  period: PayPeriod,
  bucket: Bucket503020,
  periodIncome: number,
): BucketResult {
  const allocated = roundMoney(periodIncome * BUCKET_RATIOS[bucket]);
  const categoryIds = new Set(
    data.categories
      .filter((c) => c.type === 'expense' && c.bucket503020 === bucket)
      .map((c) => c.id),
  );
  const spent = sumExpensesInRangeWhere(
    data.transactions,
    period.start,
    period.end,
    (t) => categoryIds.has(t.categoryId),
  );
  const remaining = roundMoney(allocated - spent);
  const progress =
    allocated > 0 ? Math.min(1, Math.max(0, spent / allocated)) : 0;
  return { allocated, spent, remaining, progress };
}

export function calculate503020(
  data: AppData,
  period: PayPeriod,
  today: Date,
): FiftyThirtyTwentyResult {
  const periodIncome = resolvePeriodIncome(data, period);
  const rolloverAmount = computeRolloverAmount(data, period);

  const incomeToDate = sumIncomeInRange(data.transactions, period.start, today);
  // Past scheduled bills auto-deduct on their due date — included
  // alongside transaction-based spending in the running balance.
  const spendingToDate = sumSpendingWithScheduledInRange(
    data,
    period.start,
    today,
  );
  const availableToday = roundMoney(
    rolloverAmount + incomeToDate - spendingToDate,
  );
  const dailyIncomeRate =
    period.totalDays > 0 ? roundMoney(periodIncome / period.totalDays) : 0;
  const remainingThisPeriod = roundMoney(periodIncome - spendingToDate);
  const spentToday = sumExpensesInRange(data.transactions, today, today);
  const spentThisPeriod = spendingToDate;
  const { status, statusLabel } = _classifyDailyStatus(
    availableToday,
    dailyIncomeRate,
  );

  return {
    period,
    availableToday,
    dailyIncomeRate,
    remainingThisPeriod,
    dayInPeriod: period.dayInPeriod,
    totalDays: period.totalDays,
    daysRemaining: period.daysRemaining,
    spentToday,
    spentThisPeriod,
    status,
    statusLabel,
    periodIncome,
    rolloverAmount,
    needs: computeBucket(data, period, 'needs', periodIncome),
    wants: computeBucket(data, period, 'wants', periodIncome),
    savings: computeBucket(data, period, 'savings', periodIncome),
  };
}
